/**
 * Helper minimal pour extraire les messages d'erreur des réponses Orval
 * Utilise directement les types générés par Orval
 */

import type { ErrorType } from './orval-axios-mutator.ts';
import type {
  N400Response,
  N401Response,
  N403Response,
  N404Response,
  N422Response,
  UnprocessableEntityModel,
  HttpErrorModel
} from './generated/client.schemas.ts';

type OrvalErrorResponse = N400Response | N401Response | N403Response | N404Response | N422Response;

export interface OrvalErrorToastContent {
  title: string;
  titleIsTranslationKey?: boolean;
  description?: string;
}

/**
 * Extrait le message d'erreur d'une erreur Orval
 * Priorité : violations[0].detail > message > fallback
 */
export function getOrvalErrorMessage(error: unknown, fallback?: string): string {
  if (error === null || typeof error !== 'object') {
    return fallback ?? 'Une erreur est survenue';
  }

  const orvalError = error as ErrorType<OrvalErrorResponse>;

  if (!orvalError.response?.data) {
    return fallback ?? 'Une erreur est survenue';
  }

  const data = orvalError.response.data;

  // Cas 422 : UnprocessableEntity avec violations
  if (orvalError.response.status === 422) {
    const unprocessableData = data as UnprocessableEntityModel;
    if (unprocessableData.violations && unprocessableData.violations.length > 0) {
      return unprocessableData.violations[0].detail;
    }
    if (unprocessableData.message) {
      return unprocessableData.message;
    }
  }

  // Autres erreurs HTTP (400, 401, 403, 404)
  const httpErrorData = data as HttpErrorModel;
  if (httpErrorData.message) {
    return httpErrorData.message;
  }

  return fallback ?? 'Une erreur est survenue';
}

/**
 * Extrait un contenu structuré pour afficher les erreurs Orval dans un toast.
 * Pour les 422, la première violation fournit le titre et la description.
 */
export function getOrvalErrorToastContent(
  error: unknown,
  fallback?: string
): OrvalErrorToastContent {
  const defaultMessage = fallback ?? 'Une erreur est survenue';

  if (error === null || typeof error !== 'object') {
    return { title: defaultMessage };
  }

  const orvalError = error as ErrorType<OrvalErrorResponse>;

  if (!orvalError.response?.data) {
    return { title: defaultMessage };
  }

  const data = orvalError.response.data;

  if (orvalError.response.status === 422) {
    const unprocessableData = data as UnprocessableEntityModel;
    const firstViolation = unprocessableData.violations?.[0];
    const firstViolationMessage = firstViolation?.message;
    const firstViolationDetail = firstViolation?.detail;

    return {
      title: firstViolationMessage || unprocessableData.message || defaultMessage,
      titleIsTranslationKey: Boolean(firstViolationMessage),
      description: firstViolationDetail || undefined
    };
  }

  return { title: getOrvalErrorMessage(error, fallback) };
}

/**
 * Vérifie si une erreur est une erreur Orval (AxiosError avec response)
 */
export function isOrvalError(error: unknown): error is ErrorType<OrvalErrorResponse> {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    'data' in error.response
  );
}

/** Indique si une erreur Orval correspond à une ressource introuvable (404). */
export function isOrvalNotFoundError(error: unknown): boolean {
  return isOrvalError(error) && error.response?.status === 404;
}

/** Indique si une erreur Orval correspond à une erreur serveur (500). */
export function isOrvalServerError(error: unknown): boolean {
  return isOrvalError(error) && error.response?.status === 500;
}

/**
 * Indique si l'API signale une maintenance plateforme (503).
 * Corps typique : {@link HttpErrorModel} (ex. futur `N503Response` OpenAPI).
 */
export function isOrvalServiceUnavailableError(error: unknown): boolean {
  return isOrvalError(error) && error.response?.status === 503;
}

/**
 * Vérifie si une erreur provient d'un endpoint spécifique
 * Utilise l'URL de la requête pour identifier l'endpoint
 *
 * @param error - L'erreur à vérifier
 * @param method - La méthode HTTP ('get' | 'post' | 'put' | 'patch' | 'delete')
 * @param path - Le path de l'endpoint (ex: '/medias', '/admin/workspaces')
 * @returns true si l'erreur provient de cet endpoint, false sinon
 *
 * @example
 * ```typescript
 * if (isOrvalErrorFromPath(error, 'post', '/medias')) {
 *   // Gérer l'erreur d'upload
 * }
 * if (isOrvalErrorFromPath(error, 'put', '/admin/workspaces')) {
 *   // Gérer l'erreur de workspace
 * }
 * ```
 */
export function isOrvalErrorFromPath(
  error: unknown,
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  path: string
): error is ErrorType<OrvalErrorResponse> {
  if (!isOrvalError(error)) {
    return false;
  }

  // Axios stocke l'URL dans error.config.url ou error.request.responseURL
  const requestUrl = error.config?.url || error.request?.responseURL || error.response?.config?.url;

  if (!requestUrl) {
    return false;
  }

  // Normaliser le path (enlever les paramètres de requête)
  const urlPath = new URL(requestUrl, 'http://dummy').pathname;

  // Vérifier que le path correspond
  const pathMatches = urlPath === path || urlPath.startsWith(`${path}/`);

  // Vérifier la méthode (si disponible dans config)
  const requestMethod = error.config?.method?.toLowerCase();
  const methodMatches = !requestMethod || requestMethod === method.toLowerCase();

  return pathMatches && methodMatches;
}
